import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col md:flex-row items-center gap-4 mb-6" }
const _hoisted_2 = {
  class: "w-[327px] shrink-0",
  style: {"height":"40px"}
}
const _hoisted_3 = { class: "ml-auto" }
const _hoisted_4 = { class: "flex flex-col gap-6" }
const _hoisted_5 = { class: "overflow-x-auto rounded-lg shadow-sm border border-gray-200 min-h-[300px]" }

import { ref, computed, onMounted, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'

import { mdiPlus, mdiPencilOutline, mdiContentCopy } from '@mdi/js'
import { useToast } from 'vue-toastification'

import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import PATHS from '@/router/paths'
import Network from '@/Network'
import type { QualificationTransferPolicyItem } from '@/Network/Types/Responses/QualificationTransferResponses'

import QualificationTransferTableSkeleton from '@/components/designSystem/Skeleton/QualificationTransferTableSkeleton.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import EmptyList from '@/components/EmptyList.vue'
import FilterModal from './components/FilterModal.vue'
import type { QualificationTransferFilters } from './components/FilterModal.vue'
import CreatePolicyModal from '@/modules/company/Policy/components/CreatePolicyModal.vue'
import type { CreatePolicyFormData } from '@/modules/company/Policy/components/CreatePolicyModal.vue'
import { useQualificationPolicyPermissions } from '@/modules/company/Policy/composables/useQualificationPolicyPermissions'


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const filterIcon = defineAsyncComponent(() => import('@/assets/icons/filter.vue'))

const router = useRouter()
const toast = useToast()
const headerStore = useHeaderStore()
const { canCreate, canToggle } = useQualificationPolicyPermissions('transfer')
const searchText = ref('')
const showFilterModal = ref(false)
const showCreateModal = ref(false)
const isCreating = ref(false)
const loading = ref(false)
const policies = ref<QualificationTransferPolicyItem[]>([])
const appliedFilters = ref<QualificationTransferFilters>({ companies: [] })

const appliedCompanies = computed(() => appliedFilters.value.companies)

function handleEditPolicy(policyId: number) {
  router.push(`${PATHS.MANAGE_QUALIFICATION_TRANSFER}/${policyId}`)
}

function handleApplyFilters(filters: QualificationTransferFilters) {
  appliedFilters.value = filters
}

async function handleCreatePolicy(formData: CreatePolicyFormData) {
  try {
    isCreating.value = true
    await Network.qualificationTransferPolicy.create({
      name: formData.name,
      companyId: Number(formData.companyId)
    })
    toast.success('Política criada com sucesso!')
    await fetchPolicies()
  } catch (error) {
    console.error(error)
    toast.error('Erro ao criar política. Verifique os dados.')
  } finally {
    isCreating.value = false
    showCreateModal.value = false
  }
}

async function handleToggleActive(policyId: number, value: boolean) {
  const index = policies.value.findIndex((p) => p.id === policyId)
  if (index === -1) return
  policies.value[index] = { ...policies.value[index], isActive: value }
  try {
    await Network.qualificationTransferPolicy.toggleActive(policyId, { isActive: value })
    toast.success(value ? 'Política ativada com sucesso!' : 'Política desativada com sucesso!')
  } catch {
    policies.value[index] = { ...policies.value[index], isActive: !value }
    toast.error('Erro ao alterar status da política.')
  }
}

async function handleDuplicatePolicy(policyId: number) {
  try {
    await Network.qualificationTransferPolicy.duplicate(policyId)
    toast.success('Política duplicada com sucesso!')
    await fetchPolicies()
  } catch {
    toast.error('Erro ao duplicar política.')
  }
}

const columns = [
  { key: 'id', label: 'ID', width: 'auto' },
  { key: 'policyName', label: 'NOME DA POLÍTICA' },
  { key: 'company', label: 'EMPRESA' },
  { key: 'createdBy', label: 'CRIADO POR' }
]

const filteredPolicies = computed(() => {
  if (!searchText.value) return policies.value
  const query = searchText.value.toLowerCase()
  return policies.value.filter((p) => p.policyName.toLowerCase().includes(query))
})

async function fetchPolicies() {
  try {
    loading.value = true
    const response = await Network.qualificationTransferPolicy.list({ limit: 1000 })
    policies.value = response.data
  } catch (error) {
    console.error(error)
    toast.error('Erro ao carregar políticas de repasse.')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  headerStore.setTitle('Qualificação para Repasse', 'Análise de conformidade para pagamento', {
    label: 'Políticas',
    route: PATHS.POLICIES_HOME
  })
  fetchPolicies()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (loading.value)
      ? (_openBlock(), _createBlock(QualificationTransferTableSkeleton, { key: 0 }))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(SearchInput, {
                modelValue: searchText.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
                width: "100%",
                label: "Buscar políticas",
                "hide-details": "",
                "data-testid": "search-policies-input",
                density: "compact"
              }, null, 8, ["modelValue"])
            ]),
            _createElementVNode("div", {
              onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (showFilterModal.value = true), ["stop"]))
            }, [
              _createVNode(SelectFilter, {
                label: "Filtros",
                icon: _unref(filterIcon),
                options: [],
                "is-open": false,
                selectedValue: appliedCompanies.value,
                "data-testid": "filter-policies-button"
              }, null, 8, ["icon", "selectedValue"])
            ]),
            _createElementVNode("div", _hoisted_3, [
              (_unref(canCreate))
                ? (_openBlock(), _createBlock(FilledButton, {
                    key: 0,
                    text: 'Nova Política',
                    width: "160px",
                    height: "40px",
                    "prepend-icon": _unref(mdiPlus),
                    class: "!bg-primary30",
                    "data-testid": "create-policy-button",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (showCreateModal.value = true))
                  }, null, 8, ["prepend-icon"]))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              (filteredPolicies.value.length > 0)
                ? (_openBlock(), _createBlock(TableModular, {
                    key: 0,
                    rows: filteredPolicies.value,
                    columns: columns,
                    "actions-label": "AÇÕES"
                  }, {
                    "column-id": _withCtx(({ value }) => [
                      _createVNode(LabelBadge, {
                        label: '#' + value,
                        color: "gray",
                        icon: null,
                        size: "small"
                      }, null, 8, ["label"])
                    ]),
                    actions: _withCtx(({ row }) => [
                      _createVNode(_component_v_icon, {
                        icon: _unref(mdiPencilOutline),
                        color: "primary",
                        size: "small",
                        class: "cursor-pointer",
                        "data-testid": "edit-policy-button",
                        onClick: ($event: any) => (handleEditPolicy(row.id))
                      }, null, 8, ["icon", "onClick"]),
                      (_unref(canCreate))
                        ? (_openBlock(), _createBlock(_component_v_icon, {
                            key: 0,
                            icon: _unref(mdiContentCopy),
                            color: "primary",
                            size: "small",
                            class: "cursor-pointer",
                            "data-testid": "duplicate-policy-button",
                            onClick: ($event: any) => (handleDuplicatePolicy(row.id))
                          }, null, 8, ["icon", "onClick"]))
                        : _createCommentVNode("", true),
                      _createVNode(DsSwitch, {
                        "model-value": row.isActive,
                        size: "small",
                        disabled: !_unref(canToggle),
                        "data-testid": "toggle-policy-switch",
                        "onUpdate:modelValue": (val) => handleToggleActive(row.id, val)
                      }, null, 8, ["model-value", "disabled", "onUpdate:modelValue"])
                    ]),
                    _: 1
                  }, 8, ["rows"]))
                : (_openBlock(), _createBlock(EmptyList, {
                    key: 1,
                    title: "Nenhuma política encontrada",
                    class: "py-12"
                  }))
            ])
          ])
        ], 64)),
    (showFilterModal.value)
      ? (_openBlock(), _createBlock(FilterModal, {
          key: 2,
          "modal-active": showFilterModal.value,
          "applied-filters": appliedFilters.value,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (showFilterModal.value = false)),
          onApplyFilters: handleApplyFilters
        }, null, 8, ["modal-active", "applied-filters"]))
      : _createCommentVNode("", true),
    (showCreateModal.value)
      ? (_openBlock(), _createBlock(CreatePolicyModal, {
          key: 3,
          "modal-active": showCreateModal.value,
          loading: isCreating.value,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (showCreateModal.value = false)),
          onSubmit: handleCreatePolicy
        }, null, 8, ["modal-active", "loading"]))
      : _createCommentVNode("", true)
  ]))
}
}

})